import type { QueryClient } from '@tanstack/vue-query'

import type { ChannelRoles, IChannelMemberComposit } from '../types'

// ===== TYPES =====

interface PartialMemberUpdate {
  contactId: string
  role?: ChannelRoles
  [key: string]: unknown
}

// ===== HELPER FUNCTIONS =====

/**
 * Gets all channel members query keys for a given channel and group
 * This can be called from mutation handlers outside of Vue setup context
 */
const getAllChannelMembersQueryKeys = (queryClient: QueryClient, groupId: string, channelId: string) => {
  // Get all queries from cache
  const allQueries = queryClient.getQueryCache().getAll()
  const memberQueryKeys = allQueries
    .map(query => query.queryKey)
    .filter(queryKey => {
      if (!Array.isArray(queryKey) || queryKey.length < 4) return false

      // Check if it matches our channel members query pattern
      // ['channels', groupId, 'members', channelId, roles?, searchText?]
      const isChannelMembers =
        queryKey[0] === 'channels' && queryKey[1] === groupId && queryKey[2] === 'members' && queryKey[3] === channelId

      return isChannelMembers
    })

  return memberQueryKeys
}

/**
 * Updates a single member object with partial data
 */
const updateMemberObject = (member: IChannelMemberComposit, partialMember: PartialMemberUpdate): IChannelMemberComposit => {
  return {
    ...member,
    ...partialMember,
  } as IChannelMemberComposit
}

// ===== MAIN UPDATE FUNCTIONS =====

/**
 * Updates channel members data across all channel members query caches
 * Handles infinite query structure (pages array) and regular arrays
 * Queries with search text are invalidated instead of updated optimistically
 */
export const updateChannelMembersInCache = (
  queryClient: QueryClient,
  groupId: string,
  channelId: string,
  operation: 'add' | 'update' | 'remove',
  memberData: PartialMemberUpdate | IChannelMemberComposit,
  membersToAdd?: IChannelMemberComposit[]
) => {
  const allMemberQueryKeys = getAllChannelMembersQueryKeys(queryClient, groupId, channelId)

  allMemberQueryKeys.forEach(queryKey => {
    // Check if query has search text (index 5 in query key: ['channels', groupId, 'members', channelId, roles, searchText])
    const hasSearchText = Array.isArray(queryKey) && queryKey.length > 5 && queryKey[5]

    // If query has search text, invalidate it instead of updating optimistically
    if (hasSearchText) {
      queryClient.invalidateQueries({ queryKey })
      return
    }

    const currentData = queryClient.getQueryData(queryKey)

    // Handle infinite query structure (Object with pages array)
    if (currentData && typeof currentData === 'object' && 'pages' in currentData) {
      const infiniteData = currentData as {
        pages: IChannelMemberComposit[][]
        pageParams?: unknown[]
      }
      let updatedPages: IChannelMemberComposit[][]

      if (operation === 'remove') {
        // Filter out the removed member from all pages
        updatedPages = infiniteData.pages.map((page: IChannelMemberComposit[]) => {
          return page.filter((member: IChannelMemberComposit) => member.contactId !== (memberData as PartialMemberUpdate).contactId)
        })
      } else if (operation === 'update') {
        // Update the member in all pages
        updatedPages = infiniteData.pages.map((page: IChannelMemberComposit[]) => {
          return page.map((member: IChannelMemberComposit) => {
            if (member.contactId === (memberData as PartialMemberUpdate).contactId) {
              return updateMemberObject(member, memberData as PartialMemberUpdate)
            }
            return member
          })
        })
      } else {
        // Add operation - add members to the first page
        if (membersToAdd && membersToAdd.length > 0) {
          updatedPages = [...infiniteData.pages]
          if (updatedPages.length > 0) {
            // Check if members already exist to avoid duplicates
            const existingContactIds = new Set(updatedPages.flat().map(m => m.contactId))
            const newMembers = membersToAdd.filter(m => m.contactId && !existingContactIds.has(m.contactId))
            if (newMembers.length > 0) {
              updatedPages[0] = [...newMembers, ...updatedPages[0]]
            }
          } else {
            // No pages yet, create first page with new members
            updatedPages = [membersToAdd]
          }
        } else {
          updatedPages = infiniteData.pages
        }
      }

      // Only update if something actually changed
      // Check if pages array reference changed (meaning we modified it)
      if (updatedPages !== infiniteData.pages) {
        queryClient.setQueryData(queryKey, {
          ...infiniteData,
          pages: updatedPages,
          pageParams: infiniteData.pageParams || [],
        })
      }
    }
    // Handle regular array queries (shouldn't happen with current implementation, but handle it)
    else if (Array.isArray(currentData)) {
      let updatedData: IChannelMemberComposit[]

      if (operation === 'remove') {
        updatedData = currentData.filter(
          (member: IChannelMemberComposit) => member.contactId !== (memberData as PartialMemberUpdate).contactId
        )
      } else if (operation === 'update') {
        updatedData = currentData.map((member: IChannelMemberComposit) => {
          if (member.contactId === (memberData as PartialMemberUpdate).contactId) {
            return updateMemberObject(member, memberData as PartialMemberUpdate)
          }
          return member
        })
      } else {
        // Add operation
        const existingContactIds = new Set(currentData.map(m => m.contactId))
        const newMembers = membersToAdd?.filter(m => m.contactId && !existingContactIds.has(m.contactId)) || []
        updatedData = [...newMembers, ...currentData]
      }

      // Only update if something actually changed
      if (updatedData !== currentData) {
        queryClient.setQueryData(queryKey, updatedData)
      }
    }
  })
}

/**
 * Adds members to channel members cache optimistically
 */
export const addChannelMembersToCache = (
  queryClient: QueryClient,
  groupId: string,
  channelId: string,
  members: IChannelMemberComposit[]
) => {
  updateChannelMembersInCache(queryClient, groupId, channelId, 'add', {} as PartialMemberUpdate, members)
}

/**
 * Removes a member from channel members cache optimistically
 */
export const removeChannelMemberFromCache = (queryClient: QueryClient, groupId: string, channelId: string, contactId: string) => {
  updateChannelMembersInCache(queryClient, groupId, channelId, 'remove', { contactId })
}

/**
 * Updates a member's role in channel members cache optimistically
 */
export const updateChannelMemberRoleInCache = (
  queryClient: QueryClient,
  groupId: string,
  channelId: string,
  contactId: string,
  role: ChannelRoles
) => {
  updateChannelMembersInCache(queryClient, groupId, channelId, 'update', { contactId, role })
}

/**
 * Snapshots previous state of all channel members queries for a given channel
 * Returns a Map of query key strings to their previous data
 */
export const snapshotChannelMembersQueries = (queryClient: QueryClient, groupId: string, channelId: string): Map<string, unknown> => {
  const previousQueries = new Map()
  const allQueries = queryClient.getQueryCache().getAll()
  allQueries.forEach(query => {
    const key = query.queryKey
    if (Array.isArray(key) && key[0] === 'channels' && key[1] === groupId && key[2] === 'members' && key[3] === channelId) {
      previousQueries.set(JSON.stringify(key), query.state.data)
    }
  })
  return previousQueries
}

/**
 * Rolls back channel members queries to previous state
 */
export const rollbackChannelMembersQueries = (queryClient: QueryClient, previousQueries: Map<string, unknown>) => {
  previousQueries.forEach((previousData, keyString) => {
    const key = JSON.parse(keyString)
    queryClient.setQueryData(key, previousData)
  })
}
